/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * The softphone's state machine, and how each state is presented.
 *
 * Kept as data rather than scattered conditionals so the dock and the (later)
 * Call Console render the same call identically -- there is one engine and two
 * skins, and this is what keeps the skins honest.
 */

export const CALL_STATE = {
  IDLE: "idle",
  RINGING: "ringing",       // inbound, not yet accepted
  CONNECTING: "connecting", // accepted or dialling, media not up
  CONNECTED: "connected",
  ON_HOLD: "on_hold",
  WRAP_UP: "wrap_up",
};

/** Presentation per state. `status` feeds StatusBadge's existing aliases. */
export const CALL_STATE_META = {
  [CALL_STATE.IDLE]: { label: "No active call", status: "neutral", accent: "#717C96" },
  [CALL_STATE.RINGING]: { label: "Incoming call", status: "ringing", accent: "#1565C8" },
  [CALL_STATE.CONNECTING]: { label: "Connecting", status: "connecting", accent: "#1565C8" },
  [CALL_STATE.CONNECTED]: { label: "Connected", status: "connected", accent: "#1F8A56" },
  [CALL_STATE.ON_HOLD]: { label: "On hold", status: "on hold", accent: "#E68A06" },
  [CALL_STATE.WRAP_UP]: { label: "Wrap-up", status: "wrap up", accent: "#1565C8" },
};

/** States in which a call is live enough that losing the tab matters. */
export const LIVE_STATES = new Set([
  CALL_STATE.RINGING,
  CALL_STATE.CONNECTING,
  CALL_STATE.CONNECTED,
  CALL_STATE.ON_HOLD,
]);

/** Presence values an agent may choose. Engine-owned states are absent. */
export const AGENT_PRESENCE = [
  { value: "available", label: "Available", color: "#1F8A56" },
  { value: "busy", label: "Busy", color: "#E68A06" },
  { value: "break", label: "On break", color: "#E68A06" },
  { value: "lunch", label: "Lunch", color: "#E68A06" },
  { value: "meeting", label: "In a meeting", color: "#E68A06" },
  { value: "training", label: "Training", color: "#E68A06" },
  { value: "offline", label: "Offline", color: "#717C96" },
];

export const PRESENCE_META = AGENT_PRESENCE.reduce((acc, p) => {
  acc[p.value] = p;
  return acc;
}, {});

// Engine-owned: shown, never selectable. The server rejects them too.
PRESENCE_META.ringing = { value: "ringing", label: "Ringing", color: "#1565C8" };
PRESENCE_META.on_call = { value: "on_call", label: "On a call", color: "#1F8A56" };
PRESENCE_META.wrap_up = { value: "wrap_up", label: "Wrap-up", color: "#1565C8" };

/** mm:ss for call timers. Hours only appear when a call actually runs long. */
export function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}
